import { ml_kem768 } from '@noble/post-quantum/ml-kem.js';

import { fromBase64Url, toBase64Url } from './base64url';
import {
  EXCHANGE_LABEL,
  EXCHANGE_SUITE,
  EXCHANGE_VERSION,
  FLOW_BYTE,
  GUEST_KEY_BYTES,
  KEM_CIPHERTEXT_BYTES,
  KEY_BYTES,
  OWNER_KEY_BYTES,
  P256_POINT_BYTES,
  WRAPPED_KEY_BYTES,
  type ExchangeFlow,
} from './labels';
import { concat, DecryptError, UnsupportedVersionError, deriveEcdhSecret, generateEcdh, hkdfAesKey, importPeerPoint, openBytes, sealBytes, sha256 } from './primitives';

const encoder = new TextEncoder();

/**
 * Pairing a second device (by code or nearby) is a hybrid exchange: ECDH P-256
 * and ML-KEM-768 side by side, so the wrapped link key stays secret unless
 * both are broken. The guest makes both key pairs, the owner answers with the
 * ECDH half and a KEM encapsulation. Everything goes through the server as
 * opaque bytes; docs/POST-QUANTUM.md has the construction, docs/FORMAT.md §7 the layouts.
 *
 *   owner public key  = version ‖ suite ‖ P-256 point (65 bytes)                       67 bytes
 *   guest public key  = version ‖ suite ‖ P-256 point (65 bytes) ‖ ML-KEM-768 key  1 251 bytes
 *   wrapped key       = version ‖ suite ‖ ML-KEM ciphertext (1 088) ‖ IV ‖ AES-GCM   1 150 bytes
 *
 * The two shared secrets are concatenated and run through HKDF-SHA-256 with
 * the hash of the whole transcript as `info`; the same bytes are the AES-GCM additional
 * data, so a wrapped key only opens for the exact exchange it was made in.
 */

/** What binds a wrapped key to one exchange: the flow and the id of the connect session or Nearby offer. */
export interface ExchangeContext {
  readonly flow: ExchangeFlow;
  readonly id: string;
}

export interface OwnerKey {
  readonly privateKey: CryptoKey;
  /** Version ‖ suite ‖ uncompressed P-256 point, base64url (67 bytes). */
  readonly publicKey: string;
}

export interface GuestKey {
  readonly privateKey: CryptoKey;
  /** Version ‖ suite ‖ uncompressed P-256 point ‖ ML-KEM-768 encapsulation key, base64url (1 251 bytes). */
  readonly publicKey: string;
  /** The ML-KEM decapsulation key. Wiped, and gone, the first time it is used. */
  readonly kem: { secret: Uint8Array | null };
}

export async function generateOwnerKey(): Promise<OwnerKey> {
  const { privateKey, point } = await generateEcdh();
  return { privateKey, publicKey: toBase64Url(concat(Uint8Array.of(EXCHANGE_VERSION, EXCHANGE_SUITE), point)) };
}

export async function generateGuestKey(): Promise<GuestKey> {
  const { privateKey, point } = await generateEcdh();
  const kem = ml_kem768.keygen();
  return { privateKey, publicKey: toBase64Url(concat(Uint8Array.of(EXCHANGE_VERSION, EXCHANGE_SUITE), point, kem.publicKey)), kem: { secret: kem.secretKey } };
}

/** The bytes of an owner public key, or a DecryptError if it is not one (also for a key from an older version). */
export function parseOwnerPublicKey(text: string): Uint8Array<ArrayBuffer> {
  return decodeExchangeKey(text, OWNER_KEY_BYTES);
}

/** The bytes of a guest public key, or a DecryptError if it is not one. */
export function parseGuestPublicKey(text: string): Uint8Array<ArrayBuffer> {
  return decodeExchangeKey(text, GUEST_KEY_BYTES);
}

/** Another version or suite is a different Bridge, said as such; a wrong length or point tag is an invalid key. */
function checkExchangeHead(raw: Uint8Array): void {
  if (raw[0] !== EXCHANGE_VERSION) throw new UnsupportedVersionError('The other device uses a different version of Bridge, or its key was altered on the way. Update it and try again.');
  if (raw[1] !== EXCHANGE_SUITE) throw new UnsupportedVersionError('The other device uses an encryption suite this version of Bridge does not know. Update it and try again.');
}

/** Canonical base64url of exactly `length` bytes that starts with the version and suite and, after them, an uncompressed P-256 point. */
function decodeExchangeKey(text: string, length: number): Uint8Array<ArrayBuffer> {
  let raw: Uint8Array<ArrayBuffer>;
  try {
    raw = fromBase64Url(text);
  } catch {
    throw new DecryptError('Invalid public key.');
  }
  if (toBase64Url(raw) !== text || raw.length < 3) throw new DecryptError('Invalid public key.');
  checkExchangeHead(raw);
  if (raw.length !== length || raw[2] !== 4) throw new DecryptError('Invalid public key.');
  return raw;
}

/**
 * The transcript, fixed-length fields in a fixed order: label, flow, session or offer id, both public keys and the KEM
 * ciphertext. HKDF and AES-GCM get its SHA-256 (some engines cap `info` at 1 024 bytes; the transcript is about 2.4 kB).
 */
async function transcriptHash(context: ExchangeContext, ownerKey: Uint8Array, guestKey: Uint8Array, cipherText: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  const id = encoder.encode(context.id);
  if (id.length > 0xffff) throw new DecryptError('Invalid exchange id.');
  const transcript = concat(encoder.encode(EXCHANGE_LABEL), Uint8Array.of(0, FLOW_BYTE[context.flow], id.length >> 8, id.length & 255), id, ownerKey, guestKey, cipherText);
  return concat(encoder.encode(EXCHANGE_LABEL), Uint8Array.of(0), await sha256(transcript));
}

/** ECDH secret ‖ KEM secret → HKDF-SHA-256 (the transcript as `info`) → a one-time AES-256-GCM key. Both secrets are wiped. */
async function deriveHybridKey(ecdh: Uint8Array<ArrayBuffer>, kem: Uint8Array, info: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  try {
    return await hkdfAesKey(concat(ecdh, kem), info);
  } finally {
    ecdh.fill(0);
    kem.fill(0);
  }
}

/** Owner side: encapsulate to the guest's key and encrypt the bridge key for the device that joined. */
export async function wrapBridgeKey(owner: OwnerKey, guestPublicKey: string, bridgeKeyEncoded: string, context: ExchangeContext): Promise<string> {
  const guestBytes = parseGuestPublicKey(guestPublicKey);
  const ownerBytes = parseOwnerPublicKey(owner.publicKey);
  const peer = await importPeerPoint(guestBytes.slice(2, 2 + P256_POINT_BYTES));
  let encapsulated: { cipherText: Uint8Array; sharedSecret: Uint8Array };
  try {
    encapsulated = ml_kem768.encapsulate(guestBytes.slice(OWNER_KEY_BYTES));
  } catch {
    throw new DecryptError('Invalid public key.');
  }
  let shared: CryptoKey;
  let info: Uint8Array<ArrayBuffer>;
  try {
    info = await transcriptHash(context, ownerBytes, guestBytes, encapsulated.cipherText);
    shared = await deriveHybridKey(await deriveEcdhSecret(owner.privateKey, peer), encapsulated.sharedSecret, info);
  } finally {
    encapsulated.sharedSecret.fill(0);
  }
  const linkKey = fromBase64Url(bridgeKeyEncoded);
  if (linkKey.length !== KEY_BYTES || toBase64Url(linkKey) !== bridgeKeyEncoded) throw new DecryptError('Invalid bridge key.');
  return toBase64Url(concat(Uint8Array.of(EXCHANGE_VERSION, EXCHANGE_SUITE), encapsulated.cipherText, await sealBytes(shared, linkKey, info)));
}

/** Guest side: recover the bridge key from the wrapped blob. The KEM secret is wiped whatever happens. */
export async function unwrapBridgeKey(guest: GuestKey, ownerPublicKey: string, wrappedKey: string, context: ExchangeContext): Promise<string> {
  const secret = guest.kem.secret;
  guest.kem.secret = null;
  if (secret === null) throw new DecryptError('This key exchange was already used.');
  try {
    const ownerBytes = parseOwnerPublicKey(ownerPublicKey);
    const guestBytes = parseGuestPublicKey(guest.publicKey);
    let data: Uint8Array<ArrayBuffer>;
    try {
      data = fromBase64Url(wrappedKey);
    } catch {
      throw new DecryptError('Invalid wrapped key.');
    }
    if (toBase64Url(data) !== wrappedKey || data.length < 3) throw new DecryptError('Invalid wrapped key.');
    checkExchangeHead(data);
    if (data.length !== WRAPPED_KEY_BYTES) throw new DecryptError('Invalid wrapped key.');
    const cipherText = data.slice(2, 2 + KEM_CIPHERTEXT_BYTES);
    const peer = await importPeerPoint(ownerBytes.slice(2));
    const info = await transcriptHash(context, ownerBytes, guestBytes, cipherText);
    // a ciphertext made for another key does not fail here: it gives another secret, and the unwrap below fails
    let kemSecret: Uint8Array;
    try {
      kemSecret = ml_kem768.decapsulate(cipherText, secret);
    } catch {
      throw new DecryptError('Invalid wrapped key.');
    }
    let shared: CryptoKey;
    try {
      shared = await deriveHybridKey(await deriveEcdhSecret(guest.privateKey, peer), kemSecret, info);
    } finally {
      kemSecret.fill(0);
    }
    const raw = await openBytes(shared, data.slice(2 + KEM_CIPHERTEXT_BYTES), info);
    if (raw.length !== KEY_BYTES) throw new DecryptError('Invalid wrapped key.');
    return toBase64Url(raw);
  } finally {
    secret.fill(0);
  }
}

/** Gives up an exchange that will not be finished (declined, closed, left): the KEM secret is wiped. */
export function discardGuestKey(guest: GuestKey): void {
  guest.kem.secret?.fill(0);
  guest.kem.secret = null;
}
